/**
 * RoluriTab - Componenta pentru tab-ul Administrare Roluri
 * Extrasă din AdminDashboard.jsx pentru modularizare și performanță
 */
import { memo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Shield, Plus, Trash2, Clock, Palmtree, Users, Car, Map, FileText, Settings, AlertTriangle, Award 
} from 'lucide-react';

/**
 * Componenta pentru un grup de permisiuni
 */
const PermissionGroup = memo(function PermissionGroup({ 
  title, 
  icon: Icon, 
  iconColor, 
  permissions, 
  availablePermissions, 
  selectedRoleId, 
  editingPermissions, 
  setEditingPermissions 
}) {
  const filteredPermissions = Object.entries(availablePermissions || {})
    .filter(([key]) => permissions.some(p => key.startsWith(p)));

  if (filteredPermissions.length === 0) return null;

  return (
    <div className="border rounded-lg p-3">
      <h4 className={`font-medium text-sm mb-2 ${iconColor} flex items-center gap-2`}>
        <Icon className="w-4 h-4" /> {title}
      </h4>
      <div className="space-y-1">
        {filteredPermissions.map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
            <input
              type="checkbox"
              checked={selectedRoleId === 'admin' || editingPermissions.includes(key)}
              disabled={selectedRoleId === 'admin'}
              onChange={(e) => {
                if (e.target.checked) {
                  setEditingPermissions([...editingPermissions, key]);
                } else {
                  setEditingPermissions(editingPermissions.filter(p => p !== key));
                }
              }}
              className="rounded border-slate-300"
            />
            {label}
          </label>
        ))}
      </div>
    </div>
  );
});

/**
 * Componenta RoluriTab
 */
const RoluriTab = memo(function RoluriTab({
  rolesData,
  selectedRole,
  setSelectedRole,
  editingPermissions,
  setEditingPermissions,
  savingRoles,
  saveRolePermissions,
  resetRolePermissions,
  deleteRole,
  createRole,
  hasPermission
}) {
  const [showCreateRoleDialog, setShowCreateRoleDialog] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleId, setNewRoleId] = useState('');

  const handleCreateRole = async () => {
    const success = await createRole(newRoleName, newRoleId);
    if (success) {
      setShowCreateRoleDialog(false);
      setNewRoleName('');
      setNewRoleId('');
    }
  };

  const permissionGroups = [
    { title: 'Pontaj', icon: Clock, iconColor: 'text-blue-700', permissions: ['pontaj.'] },
    { title: 'Concedii', icon: Palmtree, iconColor: 'text-green-700', permissions: ['concedii.'] },
    { title: 'Programator CO', icon: Palmtree, iconColor: 'text-emerald-700', permissions: ['programator_co.'] },
    { title: 'Angajați', icon: Users, iconColor: 'text-purple-700', permissions: ['angajati.'] },
    { title: 'Mașini', icon: Car, iconColor: 'text-orange-700', permissions: ['masini.'] },
    { title: 'Curse', icon: Map, iconColor: 'text-red-700', permissions: ['curse.'] },
    { title: 'Rapoarte', icon: FileText, iconColor: 'text-teal-700', permissions: ['rapoarte.'] },
    { title: 'Monitorizare Diferențe', icon: AlertTriangle, iconColor: 'text-orange-600', permissions: ['monitorizare.'] },
    { title: 'Motivare', icon: Award, iconColor: 'text-indigo-700', permissions: ['motivare.'] },
    { title: 'Financiar', icon: FileText, iconColor: 'text-amber-700', permissions: ['financiar.', 'facturi.'] },
    { title: 'Rapoarte de Erori', icon: AlertTriangle, iconColor: 'text-red-600', permissions: ['rapoarte_erori.'] },
    { title: 'Alte Permisiuni', icon: Settings, iconColor: 'text-slate-700', permissions: ['trasabilitate.', 'import.', 'setari.', 'roluri.', 'alerte.'] },
  ];

  return (
    <>
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Administrare Roluri și Permisiuni
            </CardTitle>
            <CardDescription>
              Creați roluri noi și configurați ce funcționalități sunt disponibile pentru fiecare rol
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Lista rolurilor */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label className="text-sm font-semibold">Selectați un rol:</Label>
                  {hasPermission('roluri.editare') && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setShowCreateRoleDialog(true)}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Rol Nou
                    </Button>
                  )}
                </div>
                <div className="space-y-1 max-h-[400px] overflow-y-auto">
                  {rolesData.roles?.map(role => (
                    <button
                      key={role.id}
                      onClick={() => {
                        setSelectedRole(role);
                        setEditingPermissions([...role.permissions]);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                        selectedRole?.id === role.id
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'bg-white border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium">{role.nume}</span>
                          {role.is_system && (
                            <span className="ml-2 text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">Sistem</span>
                          )}
                          {role.is_custom_role && (
                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Custom</span>
                          )}
                        </div>
                        <span className="text-xs text-slate-500">{role.permissions?.length || 0} permisiuni</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Editor permisiuni */}
              <div className="md:col-span-2">
                {selectedRole ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b pb-3">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          {selectedRole.nume}
                          {selectedRole.is_custom_role && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Custom</span>
                          )}
                        </h3>
                        <p className="text-sm text-slate-500">
                          {selectedRole.id === 'admin' 
                            ? 'Administratorul are toate permisiunile (nu pot fi modificate)'
                            : 'Bifați permisiunile pe care doriți să le acordați acestui rol'}
                        </p>
                      </div>
                      {selectedRole.id !== 'admin' && hasPermission('roluri.editare') && (
                        <div className="flex gap-2">
                          {selectedRole.is_custom_role && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => deleteRole(selectedRole.id, selectedRole.nume)}
                              disabled={savingRoles}
                              className="text-red-600 border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Șterge
                            </Button>
                          )}
                          {!selectedRole.is_system && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => resetRolePermissions(selectedRole.id)}
                              disabled={savingRoles}
                            >
                              Resetează
                            </Button>
                          )}
                          <Button 
                            size="sm"
                            onClick={() => saveRolePermissions(selectedRole.id, editingPermissions)}
                            disabled={savingRoles}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {savingRoles ? 'Se salvează...' : 'Salvează'}
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Grid permisiuni */}
                    <div className="grid grid-cols-2 gap-3">
                      {permissionGroups.map(group => (
                        <PermissionGroup
                          key={group.title}
                          title={group.title}
                          icon={group.icon}
                          iconColor={group.iconColor}
                          permissions={group.permissions}
                          availablePermissions={rolesData.available_permissions}
                          selectedRoleId={selectedRole.id}
                          editingPermissions={editingPermissions}
                          setEditingPermissions={setEditingPermissions}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-slate-500">
                    <div className="text-center">
                      <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Selectați un rol din lista din stânga pentru a-i configura permisiunile</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Dialog creare rol nou */}
      <Dialog open={showCreateRoleDialog} onOpenChange={setShowCreateRoleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-600" />
              Creare Rol Nou
            </DialogTitle>
            <DialogDescription>
              Creați un rol personalizat cu permisiuni specifice
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-role-name">Numele rolului *</Label>
              <Input
                id="new-role-name"
                placeholder="ex: Manager Departament"
                value={newRoleName}
                onChange={(e) => {
                  setNewRoleName(e.target.value);
                  // Auto-generate ID from name
                  if (!newRoleId || newRoleId === newRoleName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')) {
                    setNewRoleId(e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''));
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-role-id">ID rol (opțional)</Label>
              <Input
                id="new-role-id"
                placeholder="ex: mecanic, hr, resurse_umane..."
                value={newRoleId}
                onChange={(e) => setNewRoleId(e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''))}
              />
              <p className="text-xs text-slate-500">
                ID-ul este folosit intern. Se generează automat din nume.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateRoleDialog(false);
              setNewRoleName('');
              setNewRoleId('');
            }}>
              Anulează
            </Button>
            <Button 
              onClick={handleCreateRole}
              disabled={savingRoles || !newRoleName.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingRoles ? 'Se creează...' : 'Creează Rol'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default RoluriTab;
